/**
 * Binds a framework-agnostic `zustand/vanilla` store to web's React.
 *
 * Stores live in `@personal-trainer-app/shared` as plain vanilla stores (no
 * React). This helper attaches web's own React via `useStore`, so web and
 * mobile can run different React versions without the shared store binding to
 * one of them. See docs/roadmap/zustand-vanilla-migration.md.
 *
 * The returned value is callable as a hook (`useThing(selector)`) and also
 * exposes the imperative store API (`.getState()` / `.setState()` /
 * `.subscribe()`) via `Object.assign`, so non-React call sites keep working
 * unchanged.
 */
import { useStore } from 'zustand';

import type { StoreApi } from 'zustand/vanilla';

const identity = <T>(state: T): T => state;

export function bind<T>(store: StoreApi<T>) {
  const useBoundStore = <U = T>(selector?: (state: T) => U): U =>
    // Single unconditional `useStore` call; the selector defaults to identity
    // so `useThing()` returns the whole state, matching the React-bound hook.
    useStore(store, selector ?? (identity as (state: T) => U));

  return Object.assign(useBoundStore, store);
}
